import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useReveal } from "@/hooks/useReveal";
import { GoBackLayer, HintPill, MuiIcon, PersonHints, RevealControls, RevealImages, RevealStyles, WholePhotoButton } from "@/components/workspace/RevealKit";
import { MOBILE_MAX_WIDTH, defaultHint, noSave, revealRegions, tallOk, type RevealLayer, type RevealProtect, type RevealRegion } from "@/lib/revealKit";

// Full-screen person viewer — a port of photohawk-app's FullscreenViewer
// (src/views/BrowseGallery/components/FullscreenViewer.jsx), so photographers see the reveal exactly as
// buyers do. On a phone the photo fills the screen height edge to edge and pans sideways; on wider
// screens it fills the screen without cropping. A revealed person fills the screen with their reveal
// window (`frame` + the band's half, masked; `winTall` portrait renders on a phone) and the band sweeps
// head → feet on its own, with the slider + play / pause at the right edge (dragging the slider or
// sliding a finger on the person takes it over; Space plays / pauses); a tap / click on the photo goes
// back to the whole photo; Escape, the phone's back button or × close. Nothing reveals on hover.
//
// Over a lightbox (the Photos tab: `onWholePhoto`), a viewer opened on a person is left for the photo's
// lightbox every way — ×, Escape, back, a click on the photo, or the "See whole photo" card top left —
// and the page owns the history (`manageHistory={false}`, see revealNav).
//
// Portalled to <body> above everything — the admin lightbox (z 60), its drawers / menus (z 70) and
// dialogs — at the app's own z-index.

const Z = 10000;

const useViewport = () => {
  const read = () =>
    typeof window === "undefined"
      ? { w: 0, h: 0 }
      : { w: window.innerWidth, h: window.visualViewport?.height || window.innerHeight };
  const [vp, setVp] = useState(read);
  useEffect(() => {
    const on = () => setVp(read());
    window.addEventListener("resize", on);
    window.visualViewport?.addEventListener("resize", on);
    return () => {
      window.removeEventListener("resize", on);
      window.visualViewport?.removeEventListener("resize", on);
    };
  }, []);
  return vp;
};

/**
 * The viewer owns a browser history entry while open (as the app does), so a phone's back button /
 * swipe-back closes it instead of leaving the page. Returns the close action (× / Escape): it pops that
 * entry, and the pop closes the viewer.
 */
const useViewerHistory = (onClose: () => void, enabled = true) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const owned = useRef(false);
  useEffect(() => {
    if (!enabled) return undefined;
    try {
      window.history.pushState({ ...(window.history.state || {}), phxViewer: true }, "");
      owned.current = true;
    } catch { /* history unavailable: × still closes it */ }
    const onPop = () => {
      if (!owned.current) return;
      owned.current = false;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Closed some other way (unmounted by the page): drop our entry.
      if (owned.current) {
        owned.current = false;
        try { window.history.back(); } catch { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return () => {
    if (owned.current) {
      try { window.history.back(); return; } catch { /* fall through */ }
    }
    onCloseRef.current();
  };
};

interface Props {
  /** The big protected preview. */
  src: string;
  /** An already-loaded smaller preview, for an instant layout (the big one fades in over it). */
  placeholderSrc?: string;
  /** The preview URL the reveal layers hang off (`?spot=…&layer=…[&shape=tall]`). */
  revealBase?: string;
  /** Where person i's layer comes from instead (the Settings example's data-URL renders). */
  layerUrl?: (i: number, layer: RevealLayer) => string | undefined;
  protect: RevealProtect | null | undefined;
  alt?: string;
  /** Open with that person revealed (a click / tap on them in the grid / lightbox / example). */
  initialSubject?: number | null;
  onClose: () => void;
  hint?: (touch: boolean, revealed: boolean) => string;
  /** False when the page owns the history entry (the Photos tab's lightbox + reveal layers). */
  manageHistory?: boolean;
  /** "See whole photo" (a card top left while a person is shown): leave for the photo's lightbox. Also
   *  makes a click on the photo of a viewer opened on a person go there (rather than to the whole photo
   *  in the viewer). */
  onWholePhoto?: () => void;
  wholePhotoLabel?: string;
  /** The card's thumbnail (defaults to the placeholder). */
  thumbSrc?: string;
}

const FullscreenViewer = ({
  src,
  placeholderSrc,
  revealBase,
  layerUrl,
  protect,
  alt,
  onClose,
  hint,
  initialSubject = null,
  manageHistory = true,
  onWholePhoto,
  wholePhotoLabel = "See whole photo",
  thumbSrc,
}: Props) => {
  const regions: RevealRegion[] = revealRegions(protect);
  const vp = useViewport();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [hiResLoaded, setHiResLoaded] = useState(false);
  const close = useViewerHistory(onClose, manageHistory);
  const closeRef = useRef(close);
  closeRef.current = close;
  // On a phone the reveals are portrait ('tall') renders that fill the screen edge to edge (when every
  // person has a `winTall`); otherwise photo-shaped ones. (The example's data URLs are photo-shaped.)
  const phone = vp.w > 0 && vp.w <= MOBILE_MAX_WIDTH;
  const tall = phone && !layerUrl && tallOk(regions);
  const ctl = useReveal({ base: revealBase || src, regions, protect, shape: tall ? "tall" : null, layerUrl });
  const { active, touch } = ctl;

  // Reveal the chosen subject straight away.
  const openedOn = useRef<number | null>(null);
  useEffect(() => {
    if (initialSubject === null || initialSubject === undefined || !regions[initialSubject] || openedOn.current === initialSubject) return;
    openedOn.current = initialSubject;
    ctl.show(initialSubject, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSubject, regions.length]);

  // Size from the already-cached placeholder for an instant layout.
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => { if (!cancelled) setNatural({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.src = placeholderSrc || src;
    return () => { cancelled = true; };
  }, [src, placeholderSrc]);

  // Space plays / pauses a revealed person's sweep (wherever focus is, never also clicking the focused
  // button); Escape closes; the page behind doesn't scroll while open. Capture phase on window, so the
  // lightbox underneath never sees these keys (other keys are kept in by the root's onKeyDown).
  const ctlRef = useRef(ctl);
  ctlRef.current = ctl;
  useEffect(() => {
    const isSpace = (e: KeyboardEvent) => e.key === " " || e.code === "Space";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopImmediatePropagation(); closeRef.current(); return; }
      if (isSpace(e)) {
        const c = ctlRef.current;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (e.repeat || !c.controls) return;
        if (c.playing) c.pause(); else c.play();
      }
    };
    // The keyup of that Space must not click a focused button either.
    const onKeyUp = (e: KeyboardEvent) => { if (isSpace(e)) { e.preventDefault(); e.stopImmediatePropagation(); } };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("keyup", onKeyUp, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("keyup", onKeyUp, true);
      document.body.style.overflow = prev;
    };
  }, []);

  // Phone: full height, pan sideways. Wider screens: fit the screen.
  let size: { width: number; height: number } | null = null;
  if (natural && vp.w && vp.h) {
    const aspect = natural.width / natural.height;
    if (phone) {
      size = { width: Math.round(vp.h * aspect), height: Math.round(vp.h) };
    } else {
      const s = Math.min(vp.w / natural.width, vp.h / natural.height);
      size = { width: Math.round(natural.width * s), height: Math.round(natural.height * s) };
    }
  }

  // Start centred on a photo wider than the screen (on the chosen person).
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || !size || size.width <= vp.w) return;
    const r = initialSubject !== null && initialSubject !== undefined ? regions[initialSubject] : null;
    const centre = r ? ((r.x + r.width / 2) / 100) * size.width : size.width / 2;
    el.scrollLeft = Math.max(0, Math.min(size.width - vp.w, centre - vp.w / 2));
    // only when the layout size changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size && size.width, vp.w]);

  const pct = (r: RevealRegion) => ({ left: `${r.x}%`, top: `${r.y}%`, width: `${r.width}%`, height: `${r.height}%` });
  const openedOnPerson = initialSubject !== null && initialSubject !== undefined && !!regions[initialSubject];

  const body = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Photo"}
      data-phx-fullscreen=""
      style={{ position: "fixed", inset: 0, zIndex: Z, background: "#000", ...noSave }}
      onContextMenu={(e) => e.preventDefault()}
      // React events bubble through portals: keep clicks / keys here from reaching the page's handlers
      // (the lightbox backdrop closes on click; its arrows listen on window).
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.nativeEvent.stopPropagation()}
    >
      <div
        ref={scrollerRef}
        data-phx-fullscreen-scroller=""
        style={{
          position: "absolute",
          inset: 0,
          overflowX: "auto",
          overflowY: "hidden",
          display: "flex",
          alignItems: "center",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          scrollbarWidth: "none",
        }}
      >
        {size && (
          <div style={{ position: "relative", flex: "none", width: size.width, height: size.height, margin: "0 auto" }}>
            {placeholderSrc && (
              <img src={placeholderSrc} alt="" draggable={false} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", ...noSave }} />
            )}
            <img
              src={src}
              alt={alt || ""}
              draggable={false}
              onLoad={() => setHiResLoaded(true)}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: hiResLoaded || !placeholderSrc ? 1 : 0, transition: "opacity 0.3s ease", ...noSave }}
            />
            {/* An icon on each revealable person's body, until someone is revealed. */}
            <PersonHints key={src} regions={regions} rect={{ left: 0, top: 0, width: size.width, height: size.height }} touch={touch} hidden={active !== null} />
            {regions.map((r, i) => {
              const { touchAction, ref, ...tp } = ctl.targetProps(i);
              return <button type="button" key={i} ref={ref} {...tp} style={{ position: "absolute", zIndex: 5, touchAction, ...pct(r) }} />;
            })}
          </div>
        )}
      </div>

      {/* The reveal (already zoomed towards the person): edge to edge on a phone, fitted elsewhere. */}
      {regions.length > 0 && (
        <div style={{ position: "absolute", inset: 0, zIndex: 6, pointerEvents: "none" }}>
          <RevealImages ctl={ctl} fit={tall ? "cover" : "contain"} background="#000" />
        </div>
      )}

      {/* While revealed: the next tap / click anywhere on the photo goes back — to the lightbox when the
          viewer was opened on this person over one, else to the whole photo here. */}
      <GoBackLayer ctl={ctl} onBack={onWholePhoto && openedOnPerson ? close : null} />

      {onWholePhoto && (openedOnPerson || active !== null) && (
        <WholePhotoButton thumbSrc={thumbSrc || placeholderSrc || src} label={wholePhotoLabel} onClick={onWholePhoto} />
      )}

      <button
        type="button"
        aria-label="Close"
        onClick={(e) => { e.stopPropagation(); close(); }}
        style={{
          position: "absolute",
          top: "calc(env(safe-area-inset-top, 0px) + 10px)",
          right: 10,
          zIndex: 9,
          width: 40,
          height: 40,
          borderRadius: 20,
          border: "none",
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <MuiIcon name="close" />
      </button>

      {/* Scan head → feet with the slider; play / pause the sweep. Once the layers are decoded. */}
      <RevealControls ctl={ctl} />

      {regions.length > 0 && (
        <HintPill bottom="calc(env(safe-area-inset-bottom, 0px) + 20px)">
          {hint ? hint(touch, active !== null) : defaultHint(touch, active !== null, "slider")}
        </HintPill>
      )}

      <RevealStyles />
      <style>{"[data-phx-fullscreen-scroller]::-webkit-scrollbar { display: none; }"}</style>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(body, document.body) : null;
};

export default FullscreenViewer;
